import { HTMLAttributes, ReactNode, useMemo } from 'react';
import { type Theme, Tooltip, useTheme } from '@mui/material';
import styled from '@emotion/styled';
import { InventoryFileEntity } from '@/redux/slices/slice_types';
import { useAppSelector } from '@/redux/hooks';
import { selectUserDarkMode } from '@/redux/selectors/authSelectors';
import { selectFileTypeGroupingsFromApi, selectFileTypes } from '@/redux/selectors/constantsSelectors';
import ImportantTags from '@/components/_common/ImportantTags';
import ListOverflowBadge from '@/components/_common/ListOverflowBadge/ListOverflowBadge';
import MetadataTag from './MetadataTag';
import { getDuration } from '@/_utils/utils';
import rallyIcon from '@/assets/images/rally_icon.svg';

type FileInventoryTagsProps = HTMLAttributes<HTMLDivElement> & {
  file: InventoryFileEntity;
  showOnly?: number;
  showFutureLabel?: boolean;
};

const FileInventoryTags = ({ file, showOnly, showFutureLabel, ...props }: FileInventoryTagsProps) => {
  const isDarkMode = useAppSelector(selectUserDarkMode);
  const fileTypes = useAppSelector(selectFileTypes);
  const fileTypeGroupingsFromApi = useAppSelector(selectFileTypeGroupingsFromApi);
  let fileTypeGrouping = file?.file_category || fileTypes[file.file_type];

  if (fileTypeGroupingsFromApi) {
    const lookupFileTypeGrouping = Object.keys(fileTypeGroupingsFromApi).find(
      key => fileTypeGroupingsFromApi[key as keyof typeof fileTypeGroupingsFromApi].includes(file.file_type)
    );

    fileTypeGrouping = lookupFileTypeGrouping ?? fileTypeGrouping;
  }

  const rallyTags = ImportantTags({ tags: file.important_tags } as any) as any[];
  const theme = useTheme();

  const chips = useMemo(() => {
    const builtChips: ReactNode[] = [];
    if (file?.inventory_tags) {
      // BE PROVIDED TAGS
      file?.inventory_tags.forEach((item) => {
        builtChips.push(<MetadataTag key={item.label} {...item} />);
      });
    } else {
      // SELF-GENERATED TAGS
      const duration = file.media_info?.parsed_audio?.duration ? getDuration(file.media_info?.parsed_audio?.duration) : null;

      // BASE CHIPS (always)
      builtChips.push(<MetadataTag key="label" label={`Label: ${file.label}`} value={file.label} color="info" />);
      builtChips.push(<MetadataTag key="version" label={`Version: ${file.version_name}`} value={file.version_name} color="teal" />);

      // QC CHIPS
      if (!(file?.is_qc_failed === null || file?.is_qc_failed === undefined)) {
        builtChips.push(
          <MetadataTag
            key="qc"
            value={file?.is_qc_failed ? 'QC Failed' : 'QC Passed'}
            label={file?.is_qc_failed ? 'QC Failed' : 'QC Passed'}
            color={file?.is_qc_failed ? 'error' : 'success'}
            icon={{ name: file?.is_qc_failed ? 'close' : 'check', size: 15 }}
            variant="outlined"
            style={{ color: file?.is_qc_failed ? theme.palette.error.main : theme.palette.success.main }}
          />
        );
      }

      // MEDIA CHIPS (VIDEO, AUDIO, IMAGE)
      if (fileTypeGrouping === 'video' && file.media_info?.parsed_video) {
        if (file.media_info?.parsed_video?.resolution) {
          builtChips.push(
            <MetadataTag
              key="resolution"
              label={`Resolution: ${file.media_info?.parsed_video?.resolution}`}
              value={file.media_info?.parsed_video?.resolution}
            />
          );
        }

        if (file.media_info?.parsed_video?.frame_rate) {
          builtChips.push(
            <MetadataTag
              key="frame_rate"
              label={`Frame Rate: ${file.media_info?.parsed_video?.frame_rate}`}
              value={file.media_info?.parsed_video?.frame_rate}
            />
          );
        }

        if (file.media_info?.parsed_video?.aspect_ratio) {
          builtChips.push(
            <MetadataTag
              key="aspect_ratio"
              label={`Aspect Ratio: ${file.media_info?.parsed_video?.aspect_ratio}`}
              value={file.media_info?.parsed_video?.aspect_ratio}
            />
          );
        }

        if (file.media_info?.parsed_video?.format) {
          builtChips.push(
            <MetadataTag key="format" label={`Format: ${file.media_info?.parsed_video?.format}`} value={file.media_info?.parsed_video?.format} />
          );
        }
      } else if (fileTypeGrouping === 'audio' && file.media_info?.parsed_audio) {
        if (file.media_info?.parsed_audio?.format) {
          builtChips.push(
            <MetadataTag key="format" label={`Format: ${file.media_info?.parsed_audio?.format}`} value={file.media_info?.parsed_audio?.format} />
          );
        }

        if (file.media_info?.parsed_audio?.bitrate) {
          builtChips.push(
            <MetadataTag key="bitrate" label={`Bitrate: ${file.media_info?.parsed_audio?.bitrate}`} value={file.media_info?.parsed_audio?.bitrate} />
          );
        }

        if (file.media_info?.parsed_audio?.duration && duration) {
          builtChips.push(<MetadataTag key="duration" label={`Duration: ${duration}`} value={duration} />);
        }
      } else if (fileTypeGrouping === 'image' && !!file?.media_info?.image?.length && file?.media_info?.image[0]) {
        if (file.media_info?.image[0].codec) {
          builtChips.push(<MetadataTag key="codec" label={`Codec: ${file.media_info?.image[0]?.codec}`} value={file.media_info?.image[0]?.codec} />);
        }

        if (file.media_info?.image[0].width && file.media_info?.image[0].height) {
          builtChips.push(
            <MetadataTag
              key="resolution"
              label={`Resolution: ${file.media_info?.image[0]?.width}x${file.media_info?.image[0]?.height}`}
              value={`${file.media_info?.image[0]?.width}x${file.media_info?.image[0]?.height}`}
            />
          );
        }

        if (file.media_info?.image[0].compressionMode) {
          builtChips.push(
            <MetadataTag
              key="compression_mode"
              label={`Compression Mode: ${file.media_info?.image[0]?.compressionMode}`}
              value={file.media_info?.image[0]?.compressionMode}
            />
          );
        }
      }

      // FUTURE TAG
      if (showFutureLabel) {
        builtChips.push(<MetadataTag key="future" label="Future" variant="dashed" color="info" icon={{ name: 'clock_future', size: 15 }} />);
      }

      // RALLY TAGS
      if (file?.is_rally_inventory) {
        builtChips.push(
          <MetadataTag
            key="rally"
            variant="outlined"
            color="info"
            label="Rally"
            icon={<img height={18} src={rallyIcon} style={{ marginRight: 3 }} />}
          />
        );
      }

      if (rallyTags) {
        rallyTags.forEach((item) => {
          builtChips.push(
            <Tooltip key={`rally_tags`} title={`Important Tag: ${item.props.label}`}>
              {item}
            </Tooltip>
          );
        });
      }
    }

    return builtChips;
  }, [file, showFutureLabel, isDarkMode]);

  return (
    <StyledDiv {...props}>
      {chips.length > 0 ? (
        <ListOverflowBadge
          list={chips}
          show={showOnly}
          tooltipDisplay={(item: any, index: number, total: number) => (
            <TooltipItemDiv key={`tooltip-chip-${index}`} paddingBottom={index < total - 1 ? 5 : 0}>
              {item}
            </TooltipItemDiv>
          )}
        />
      ) : (
        <></>
      )}
    </StyledDiv>
  );
};

export default FileInventoryTags;

const chipStyles = `
  .MuiChip-root {
    border-radius: 5px;
    height: 26px;
    max-width: 15em;
    font-size: 11px !important;
    margin-right: 5px;

    .MuiChip-label {
      font-size: 11px !important;
      display: inline-block;
      max-width: 15em;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    label {
      padding-left: 0px;
      padding-right: 0px;
      margin-right: 5px;
    }

    label + strong {
      opacity: 0.5;
      margin-right: ${({ theme }: { theme: Theme }) => theme.spacing(3)};
    }
  }
`;

const StyledDiv = styled.div`
  margin: ${({ theme }) => theme.spacing(0.5, 0)};

  // "rallyTags" styles rely on styles defined in "chipStyles"
  ${chipStyles}
`;

const TooltipItemDiv = styled.div<{ paddingBottom: number }>`
  padding-bottom: ${({ paddingBottom }) => paddingBottom}px;

  ${chipStyles}
`;
